import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { AlertTriangle, Send } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, DetailItem, PageHeader } from '../components/ui/Card';
import { Checkbox, Input } from '../components/ui/Input';
import { useChangePassword, useNotificationSettings, useSaveNotificationSettings, useTestEmail } from '../hooks/useApi';
import { errorMessage } from '../services/api';
import { toast, useAuthStore } from '../store';
import { formatDateTime } from '../utils/helpers';
import { t } from '../i18n';

interface PasswordValues {
  current: string;
  next: string;
  confirm: string;
}

export function Account() {
  const { user, setUser } = useAuthStore();
  const changePassword = useChangePassword();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PasswordValues>();

  const onSubmit = async (values: PasswordValues) => {
    try {
      setUser(await changePassword.mutateAsync({ current: values.current, next: values.next }));
      toast.success(t('Heslo bolo zmenené'));
      reset({ current: '', next: '', confirm: '' });
    } catch (error) {
      toast.error(errorMessage(error, t('Heslo sa nepodarilo zmeniť')));
    }
  };

  return (
    <>
      <PageHeader title={t('Môj účet')} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('Profil')} />
          <dl className="grid grid-cols-2 gap-4">
            <DetailItem label={t('Používateľské meno')}>{user?.username}</DetailItem>
            <DetailItem label={t('Meno')}>{user?.full_name || '–'}</DetailItem>
            <DetailItem label={t('Rola')}>{user?.role === 'admin' ? t('Administrátor') : t('Používateľ')}</DetailItem>
            <DetailItem label={t('Posledné prihlásenie')}>{formatDateTime(user?.last_login)}</DetailItem>
          </dl>
        </Card>
        <Card>
          <CardHeader title={t('Zmena hesla')} />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label={t('Súčasné heslo')}
              type="password"
              autoComplete="current-password"
              error={errors.current?.message}
              {...register('current', { required: t('Zadajte súčasné heslo') })}
            />
            <Input
              label={t('Nové heslo')}
              type="password"
              autoComplete="new-password"
              error={errors.next?.message}
              {...register('next', { required: t('Zadajte nové heslo'), minLength: { value: 8, message: t('Aspoň 8 znakov') } })}
            />
            <Input
              label={t('Nové heslo znova')}
              type="password"
              autoComplete="new-password"
              error={errors.confirm?.message}
              {...register('confirm', { validate: (v) => v === watch('next') || t('Heslá sa nezhodujú') })}
            />
            <Button type="submit" loading={changePassword.isPending}>
              {t('Zmeniť heslo')}
            </Button>
          </form>
        </Card>
        <NotificationsCard />
      </div>
    </>
  );
}

interface NotificationValues {
  email: string;
  kinds: string[];
}

function NotificationsCard() {
  const { data } = useNotificationSettings();
  const save = useSaveNotificationSettings();
  const test = useTestEmail();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<NotificationValues>({ defaultValues: { email: '', kinds: [] } });

  useEffect(() => {
    if (data) reset({ email: data.email ?? '', kinds: data.kinds });
  }, [data, reset]);

  const onSubmit = async (values: NotificationValues) => {
    try {
      const saved = await save.mutateAsync({ email: values.email.trim() || null, kinds: values.kinds });
      reset({ email: saved.email ?? '', kinds: saved.kinds });
      toast.success(saved.email ? t('Nastavenie upozornení bolo uložené') : t('E-mailová adresa bola odstránená – upozornenia nebudú chodiť'));
    } catch (error) {
      toast.error(errorMessage(error, t('Nastavenie sa nepodarilo uložiť')));
    }
  };

  const sendTest = async () => {
    try {
      toast.success((await test.mutateAsync()).detail);
    } catch (error) {
      toast.error(errorMessage(error, t('Skúšobný e-mail sa nepodarilo odoslať')));
    }
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title={t('Upozornenia e-mailom')}
        subtitle={t('Upozornenie príde hneď, ako nastane – udalosti okamžite, blížiace sa termíny raz (kontrola každú hodinu cez deň).')}
      />
      {data && !data.mail_configured && (
        <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('Server zatiaľ nemá nastavené odosielanie e-mailov (SMTP v súbore backend\\.env). Nastavenie si môžete uložiť, e-maily začnú chodiť po jeho doplnení.')}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <Input
          label={t('E-mailová adresa')}
          type="email"
          autoComplete="email"
          placeholder={t('meno@firma.sk')}
          className="max-w-md"
          error={errors.email?.message}
          {...register('email', { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('Zadajte platnú e-mailovú adresu') } })}
        />
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-secondary-700">{t('Chcem dostávať')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {data?.available.map((kind) => <Checkbox key={kind.key} value={kind.key} label={kind.label} {...register('kinds')} />)}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending} disabled={!isDirty}>
            {t('Uložiť')}
          </Button>
          <Button type="button" variant="outline" onClick={sendTest} loading={test.isPending} disabled={!data?.email || isDirty}>
            <Send className="h-4 w-4" />  {t('Poslať skúšobný e-mail')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
